import React, { useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import {
  IconBulb,
  IconSparkles,
  IconUsers,
  IconTools,
  IconTarget,
  IconCheck,
  IconShieldCheck,
  IconArrowRight,
} from '@tabler/icons-react';
import { postOnboarding } from '../../Users/services/UserServicesV2';
import Swal from 'sweetalert2';

const CLIENTS_RANGE_OPTIONS = [
  { value: 'none', label: 'Ainda não tenho clientes' },
  { value: '1_10', label: '1 a 10' },
  { value: '11_50', label: '11 a 50' },
  { value: '51_200', label: '51 a 200' },
  { value: '200_plus', label: 'Mais de 200' },
];

const TOOLS_OPTIONS = [
  { value: 'n8n', label: 'n8n' },
  { value: 'make', label: 'Make (Integromat)' },
  { value: 'zapier', label: 'Zapier' },
  { value: 'manychat', label: 'ManyChat' },
  { value: 'typebot', label: 'Typebot' },
  { value: 'botpress', label: 'Botpress' },
  { value: 'dify', label: 'Dify' },
  { value: 'chatwoot', label: 'Chatwoot' },
  { value: 'other', label: 'Outra' },
];

const OnboardingQuestions = ({ accountId, signupToken, userEmail, trialDays, onFinish }) => {
  const [processing, setProcessing] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      operational_problem: '',
      attraction_reason: '',
      clients_range: '',
      tools_used: [],
      tools_used_other: '',
      expectation: '',
    },
  });

  const toolsUsed = watch('tools_used') || [];

  const toggleTool = (toolValue) => {
    const current = toolsUsed;
    if (current.includes(toolValue)) {
      setValue('tools_used', current.filter((t) => t !== toolValue), { shouldValidate: true });
    } else {
      setValue('tools_used', [...current, toolValue], { shouldValidate: true });
    }
  };

  const showSuccessAlert = () => {
    const trialMsg = trialDays
      ? `<p style="margin-top: 8px; font-size: 14px; color: #3b82f6; font-weight: 500;">Seu período de teste de ${trialDays} dias começa após a ativação.</p>`
      : '';
    Swal.fire({
      icon: 'success',
      title: 'Tudo pronto!',
      html: `
        <p style="margin-bottom: 8px;">Enviamos um e-mail de ativação para:</p>
        <strong>${userEmail || ''}</strong>
        <p style="margin-top: 12px; font-size: 14px; color: #666;">
          Verifique sua caixa de entrada (e spam) para ativar sua conta.
        </p>
        ${trialMsg}
      `,
      confirmButtonText: 'Ir para o Login',
      confirmButtonColor: '#3b82f6',
      allowOutsideClick: false,
    }).then(() => {
      if (onFinish) onFinish();
      else window.location.href = '/login';
    });
  };

  const onSubmit = async (data) => {
    setProcessing(true);
    try {
      const payload = {
        signup_token: signupToken,
        operational_problem: (data.operational_problem || '').trim(),
        attraction_reason: (data.attraction_reason || '').trim(),
        clients_range: data.clients_range,
        tools_used: data.tools_used || [],
        tools_used_other: (data.tools_used_other || '').trim(),
        expectation: (data.expectation || '').trim(),
        skipped: false,
      };

      await postOnboarding(accountId, payload);
      showSuccessAlert();
    } catch (error) {
      console.error('Erro ao salvar onboarding:', error);
      Swal.fire({
        icon: 'warning',
        title: 'Não foi possível salvar suas respostas',
        text:
          'Mas sua conta já foi criada com sucesso! Verifique seu e-mail para ativá-la.',
        confirmButtonText: 'Ir para o Login',
        confirmButtonColor: '#3b82f6',
      }).then(() => {
        if (onFinish) onFinish();
        else window.location.href = '/login';
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleSkip = async () => {
    setSkipping(true);
    try {
      await postOnboarding(accountId, {
        signup_token: signupToken,
        skipped: true,
      });
    } catch (error) {
      // Se falhar o skip, apenas prossegue — a conta já existe
      console.warn('Falha ao registrar skip:', error);
    } finally {
      setSkipping(false);
      showSuccessAlert();
    }
  };

  return (
    <div className="signup-onboarding">
      <div className="signup-onboarding__card">
        <div className="signup-onboarding__intro">
          <IconSparkles size={20} className="signup-onboarding__intro-icon" />
          <p className="signup-onboarding__intro-text">
            Suas respostas nos ajudam a <strong>melhorar nossos serviços</strong> e
            <strong> personalizar sua jornada</strong> dentro do ecossistema Mercado Agentes.
            Leva menos de 1 minuto.
          </p>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Q1 - Maior problema */}
          <Form.Group className="signup-onboarding__question">
            <Form.Label>
              <IconBulb size={16} className="me-2" />
              Qual o maior problema em sua operação hoje?
            </Form.Label>
            <small className="signup-onboarding__helper">
              Vamos priorizar soluções que resolvam o que realmente te incomoda.
            </small>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={500}
              placeholder="Conte com suas palavras..."
              {...register('operational_problem', {
                required: 'Conte-nos o maior problema atual',
                minLength: { value: 5, message: 'Resposta muito curta' },
              })}
              disabled={processing}
              isInvalid={!!errors.operational_problem}
            />
            {errors.operational_problem && (
              <Form.Control.Feedback type="invalid">
                {errors.operational_problem.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Q2 - Atração */}
          <Form.Group className="signup-onboarding__question">
            <Form.Label>
              <IconSparkles size={16} className="me-2" />
              O que mais te chamou atenção em nosso ecossistema?
            </Form.Label>
            <small className="signup-onboarding__helper">
              Isso nos ajuda a melhorar o que mais é importante pra você.
            </small>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={500}
              placeholder="Conte com suas palavras..."
              {...register('attraction_reason', {
                required: 'Conte-nos o que chamou sua atenção',
                minLength: { value: 5, message: 'Resposta muito curta' },
              })}
              disabled={processing}
              isInvalid={!!errors.attraction_reason}
            />
            {errors.attraction_reason && (
              <Form.Control.Feedback type="invalid">
                {errors.attraction_reason.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Q3 - Clientes */}
          <Form.Group className="signup-onboarding__question">
            <Form.Label>
              <IconUsers size={16} className="me-2" />
              Quantos clientes ou projetos você gerencia atualmente?
            </Form.Label>
            <small className="signup-onboarding__helper">
              Nos permite sugerir o melhor setup inicial para seu porte.
            </small>
            <Form.Select
              {...register('clients_range', {
                required: 'Escolha uma faixa',
              })}
              disabled={processing}
              isInvalid={!!errors.clients_range}
            >
              <option value="">Selecione...</option>
              {CLIENTS_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            {errors.clients_range && (
              <Form.Control.Feedback type="invalid">
                {errors.clients_range.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Q4 - Ferramentas */}
          <Form.Group className="signup-onboarding__question">
            <Form.Label>
              <IconTools size={16} className="me-2" />
              Com quais ferramentas você já trabalha ou já trabalhou?
            </Form.Label>
            <small className="signup-onboarding__helper">
              Pode marcar mais de uma. Nos ajuda a priorizar integrações.
            </small>
            <div className="signup-onboarding__tools">
              {TOOLS_OPTIONS.map((opt) => {
                const checked = toolsUsed.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`signup-onboarding__tool-chip ${checked ? 'active' : ''}`}
                    onClick={() => toggleTool(opt.value)}
                    disabled={processing}
                  >
                    {checked && <IconCheck size={14} className="me-1" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {toolsUsed.includes('other') && (
              <Form.Control
                type="text"
                className="mt-2"
                placeholder="Qual(is) outra(s) ferramenta(s)?"
                maxLength={200}
                {...register('tools_used_other')}
                disabled={processing}
              />
            )}
          </Form.Group>

          {/* Q5 - Expectativa */}
          <Form.Group className="signup-onboarding__question">
            <Form.Label>
              <IconTarget size={16} className="me-2" />
              O que você espera da plataforma?
            </Form.Label>
            <small className="signup-onboarding__helper">
              Conta pra gente o que você espera — queremos entregar.
            </small>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={500}
              placeholder="O que você espera alcançar com a plataforma..."
              {...register('expectation', {
                required: 'Conte-nos sua expectativa',
                minLength: { value: 5, message: 'Resposta muito curta' },
              })}
              disabled={processing}
              isInvalid={!!errors.expectation}
            />
            {errors.expectation && (
              <Form.Control.Feedback type="invalid">
                {errors.expectation.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Submit */}
          <button
            type="submit"
            className="signup-plan-card__cta signup-plan-card__cta--success w-100"
            disabled={processing || skipping}
          >
            {processing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Salvando respostas...
              </>
            ) : (
              <>
                <IconCheck size={20} className="me-2" />
                Concluir cadastro
                <IconArrowRight size={18} className="ms-2" />
              </>
            )}
          </button>

          {/* Skip */}
          {/* <button
            type="button"
            className="signup-onboarding__skip"
            onClick={handleSkip}
            disabled={processing || skipping}
          >
            {skipping ? 'Processando...' : 'Pular por enquanto'}
          </button> */}

          <div className="signup-free-form__secure">
            <IconShieldCheck size={14} />
            <span>Seus dados são usados apenas para melhorar sua experiência</span>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default OnboardingQuestions;
